import type { ContentGroupValue } from './content-group';

/**
 * Page title inputs mirroring `SeoMeta`, resolved once by the route loader so the
 * `page_view` event can build the exact same `<title>` string without reading the DOM
 * (which can still hold the previous page's title during SPA navigation with deferred data).
 */
export type AnalyticsPageTitle = {
    /** Base title before the site-name suffix (matches `SeoMeta`'s resolved `title`). */
    title: string;
    /** When true, the title is used verbatim (no ` | {siteName}` suffix). */
    rawTitle?: boolean;
};

export type RouteDataWithAnalytics = {
    analytics?: {
        contentGroup?: ContentGroupValue;
        pageTitle?: AnalyticsPageTitle;
    };
};

export function attachAnalytics<T extends object>(
    data: T,
    contentGroup: ContentGroupValue | undefined,
    pageTitle?: AnalyticsPageTitle
): T & RouteDataWithAnalytics {
    if (!contentGroup && !pageTitle) {
        return data;
    }

    return {
        ...data,
        analytics: {
            ...(contentGroup ? { contentGroup } : {}),
            ...(pageTitle ? { pageTitle } : {}),
        },
    };
}

export function resolveRouteContentGroup(
    matches: ReadonlyArray<{ data?: RouteDataWithAnalytics }>
): ContentGroupValue | undefined {
    for (let index = matches.length - 1; index >= 0; index -= 1) {
        const contentGroup = matches[index]?.data?.analytics?.contentGroup;
        if (contentGroup) {
            return contentGroup;
        }
    }

    return undefined;
}

/** Resolves the analytics page title from the deepest match that exposes one. */
export function resolveRoutePageTitle(
    matches: ReadonlyArray<{ data?: RouteDataWithAnalytics }>
): AnalyticsPageTitle | undefined {
    for (let index = matches.length - 1; index >= 0; index -= 1) {
        const pageTitle = matches[index]?.data?.analytics?.pageTitle;
        if (pageTitle) {
            return pageTitle;
        }
    }

    return undefined;
}
